import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../../lib/api";
import ImageUpload from "../../components/admin/ImageUpload";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = {
    title: "",
    date: new Date().toISOString().slice(0, 10),
    time: "",
    location: "",
    description: "",
    image_url: "",
    link: "",
};

export default function EventsAdmin() {
    const [events, setEvents] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => api.get("/admin/events").then((r) => setEvents(r.data)), []);
    useEffect(() => {
        load();
    }, [load]);

    const openNew = () => {
        setForm(empty);
        setEditing({ id: null });
        setError("");
    };
    const openEdit = (ev) => {
        setForm({
            title: ev.title,
            date: ev.date,
            time: ev.time || "",
            location: ev.location || "",
            description: ev.description || "",
            image_url: ev.image_url || "",
            link: ev.link || "",
        });
        setEditing(ev);
        setError("");
    };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            if (editing.id) await api.put(`/admin/events/${editing.id}`, form);
            else await api.post("/admin/events", form);
            setEditing(null);
            await load();
        } catch (err) {
            setError(apiError(err.response?.data?.detail) || "Could not save");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (ev) => {
        if (!window.confirm(`Delete "${ev.title}"?`)) return;
        await api.delete(`/admin/events/${ev.id}`);
        load();
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const isPast = (d) => d < new Date().toISOString().slice(0, 10);

    return (
        <div data-testid="admin-events">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-serif text-3xl text-blue-50">Events</h1>
                    <p className="mt-1 text-sm text-slate-300/55">
                        Story times, parent nights, and community gatherings. Past
                        events hide automatically on the public site.
                    </p>
                </div>
                <button
                    data-testid="event-new-button"
                    onClick={openNew}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-5 py-2.5 text-sm font-semibold text-[#ffffff] hover:brightness-110"
                >
                    <Plus className="h-4 w-4" /> New event
                </button>
            </div>

            <div className="mt-8 space-y-3">
                {events.length === 0 && (
                    <p className="text-sm text-slate-300/45">No events yet.</p>
                )}
                {events.map((ev) => (
                    <div
                        key={ev.id}
                        className="flex items-center gap-4 rounded-2xl border border-blue-500/15 bg-[#0c172e] p-4"
                    >
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <h3 className="font-serif text-lg text-slate-300">
                                    {ev.title}
                                </h3>
                                {isPast(ev.date) && (
                                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-300/50">
                                        Past · hidden
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-slate-300/45">
                                {ev.date} {ev.time && `· ${ev.time}`}{" "}
                                {ev.location && `· ${ev.location}`}
                            </p>
                        </div>
                        <button
                            onClick={() => openEdit(ev)}
                            aria-label="Edit"
                            data-testid={`event-edit-${ev.id}`}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400/80 hover:bg-blue-500/15"
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => remove(ev)}
                            aria-label="Delete"
                            data-testid={`event-delete-${ev.id}`}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-red-300/80 hover:bg-red-500/15"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-6">
                    <form
                        onSubmit={save}
                        className="my-6 w-full max-w-xl rounded-2xl border border-blue-500/20 bg-[#0c172e] p-7"
                    >
                        <div className="flex items-center justify-between">
                            <h2 className="font-serif text-2xl text-blue-50">
                                {editing.id ? "Edit event" : "New event"}
                            </h2>
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400/70 hover:bg-blue-500/15"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="mt-6 space-y-5">
                            <div>
                                <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                    Title
                                </label>
                                <input
                                    data-testid="event-title-input"
                                    required
                                    value={form.title}
                                    onChange={set("title")}
                                    className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                />
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                        Date
                                    </label>
                                    <input
                                        data-testid="event-date-input"
                                        type="date"
                                        required
                                        value={form.date}
                                        onChange={set("date")}
                                        className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                        Time
                                    </label>
                                    <input
                                        value={form.time}
                                        onChange={set("time")}
                                        placeholder="10:00 AM"
                                        className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                    Location
                                </label>
                                <input
                                    value={form.location}
                                    onChange={set("location")}
                                    placeholder="The Playroom, Longwood"
                                    className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                    Description
                                </label>
                                <textarea
                                    rows={3}
                                    value={form.description}
                                    onChange={set("description")}
                                    className="w-full resize-none rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                />
                            </div>
                            <ImageUpload
                                label="Event image"
                                value={form.image_url}
                                onChange={(url) => setForm({ ...form, image_url: url })}
                            />
                            <div>
                                <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                    Registration link
                                </label>
                                <input
                                    data-testid="event-link-input"
                                    value={form.link}
                                    onChange={set("link")}
                                    placeholder="https://…"
                                    className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                />
                            </div>
                            {error && <p className="text-sm text-red-400">{error}</p>}
                        </div>
                        <div className="mt-7 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="rounded-full border border-blue-500/30 px-6 py-2.5 text-sm text-slate-300 hover:bg-blue-500/10"
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="event-save-button"
                                type="submit"
                                disabled={busy}
                                className="rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-6 py-2.5 text-sm font-semibold text-[#ffffff] hover:brightness-110 disabled:opacity-60"
                            >
                                {busy ? "Saving…" : "Save event"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
